/**
 * Lobby settings — host-controlled, host-mutable, broadcast to all clients.
 *
 * Adding a new setting is a single entry in the registry below. The Zod
 * schema is generated from the registry, the web UI auto-renders from
 * `meta`, and `applyDefaults` fills in missing keys for backwards
 * compatibility on existing lobbies.
 *
 * Settings are persisted on the LOBBY#<code> META row as a `settings` map.
 * Mutations come over WS via `lobby.settings.set` and are validated against
 * the schema before being written.
 */
import { z } from 'zod';
import { expandedSelectionIds, listEnabled } from '@tt/games-registry';

// ── Schema ──────────────────────────────────────────────────────────────

// R2#68 — `allowedGames` previously accepted any string (including
// "rm -rf /"); host could put garbage in. Validate each entry against
// the games-registry: a base id (e.g. 'splatter-square') OR a fully
// qualified selection id (e.g. 'splatter-square:duel'). The set is
// rebuilt at module import — listEnabled / expandedSelectionIds are
// pure functions over static manifest data, so this is cheap.
const VALID_GAME_IDS: ReadonlySet<string> = (() => {
  const s = new Set<string>();
  for (const m of listEnabled()) s.add(m.id);
  for (const id of expandedSelectionIds()) s.add(id);
  return s;
})();

export const LobbySettingsSchemaInternal = z.object({
  mode: z.enum(['set', 'free', 'vote']),
  eventMode: z.enum(['standard', 'lightning', 'comeback', 'chaos', 'cozy']),
  roundsPerSet: z.number().int().min(1).max(15),
  winCondition: z.enum(['rounds', 'firstToScore', 'highestAfter']),
  targetScore: z.number().int().min(100).max(10_000),
  allowedGames: z.array(z.string().min(1)).max(64).superRefine((arr, ctx) => {
    for (const id of arr) {
      if (!VALID_GAME_IDS.has(id)) {
        ctx.addIssue({
          code: 'custom',
          message: `allowedGames must be valid game ids; got ${id}`,
        });
        return;
      }
    }
  }),
  voteOptions: z.number().int().min(2).max(6),
  voteTimerSec: z.number().int().min(5).max(30),
  roundCountdownSec: z.number().int().min(1).max(15),
  pickWho: z.enum(['host', 'vote', 'random']),
  hostRotation: z.enum(['fixed', 'rotate']),
  latePlayers: z.enum(['spectate', 'join']),
  chatEnabled: z.boolean(),
  reactionsEnabled: z.boolean(),
  pointMultiplier: z.union([z.literal(0.5), z.literal(1), z.literal(1.5), z.literal(2)]),
  lobbyPersistHours: z.number().int().min(1).max(24),
  quickRejoinSec: z.number().int().min(0).max(120),
  showTutorials: z.boolean(),
});

export type LobbySettings = z.infer<typeof LobbySettingsSchemaInternal>;
export type LobbySettingsPartial = Partial<LobbySettings>;
export const LobbySettingsSchema = LobbySettingsSchemaInternal.partial();

// ── Field metadata ──────────────────────────────────────────────────────

export type FieldGroup = 'mode' | 'pacing' | 'pool' | 'social' | 'scoring';
export interface FieldMeta {
  label: string;
  hint?: string;
  group: FieldGroup;
  control: 'select' | 'number' | 'toggle' | 'multi-select';
  optionsFromRegistry?: 'games';
  showWhen?: (s: LobbySettings) => boolean;
  order: number;
}

// ── Defaults + per-field meta (single registry the UI/server share) ─────

const DEFAULTS: LobbySettings = {
  mode: 'vote',
  eventMode: 'standard',
  roundsPerSet: 5,
  winCondition: 'rounds',
  targetScore: 1000,
  allowedGames: [],
  voteOptions: 3,
  voteTimerSec: 15,
  // Universal start-sequence (UniversalMatchIntro): the 3-2-1 portion
  // ONLY. Total warmup = 3000ms intro + 10000ms tutorial (round 1 only)
  // + roundCountdownSec*1000ms countdown. Default flipped 9 -> 3 because
  // the lengthy intro+tutorial portions are now constants; only the
  // countdown numerals are user-tunable. Range stays 1..15.
  roundCountdownSec: 3,
  pickWho: 'vote',
  hostRotation: 'fixed',
  latePlayers: 'join',
  chatEnabled: true,
  reactionsEnabled: true,
  pointMultiplier: 1,
  lobbyPersistHours: 4,
  quickRejoinSec: 30,
  showTutorials: true,
};

export const FIELD_META: Record<keyof LobbySettings, FieldMeta> = {
  mode: { label: 'Session mode', hint: 'Set = N rounds. Vote = pick next between rounds. Free = open table.', group: 'mode', control: 'select', order: 0 },
  eventMode: { label: 'Event mode', hint: 'Standard = baseline. Lightning = 25% shorter rounds + faster countdown. Cozy = 20% longer rounds + 9s countdown for tutorial-reading. Chaos = +1 vote choice each round. Comeback = bonus point if you win a round while behind.', group: 'pacing', control: 'select', order: 0 },
  roundsPerSet: { label: 'Rounds per set', hint: 'How many games before final standings.', group: 'mode', control: 'number', order: 1, showWhen: (s) => s.mode !== 'free' },
  winCondition: { label: 'Win condition', group: 'mode', control: 'select', order: 2, showWhen: (s) => s.mode !== 'free' },
  targetScore: { label: 'Target score', hint: 'First to reach this wins.', group: 'mode', control: 'number', order: 3, showWhen: (s) => s.winCondition === 'firstToScore' },
  allowedGames: { label: 'Allowed games', hint: 'Empty = all enabled.', group: 'pool', control: 'multi-select', optionsFromRegistry: 'games', order: 0 },
  voteOptions: { label: 'Vote choices', group: 'pacing', control: 'number', order: 1, showWhen: (s) => s.pickWho === 'vote' || s.mode === 'vote' },
  voteTimerSec: { label: 'Vote timer (s)', group: 'pacing', control: 'number', order: 2, showWhen: (s) => s.pickWho === 'vote' || s.mode === 'vote' },
  roundCountdownSec: { label: 'Pre-game countdown (s)', hint: 'Length of the 3-2-1-GO numerals only. The Intro screen (3s) + Tutorial popup (10s on round 1) come before this.', group: 'pacing', control: 'number', order: 3 },
  pickWho: { label: 'Who picks next?', hint: 'Used in set/vote modes.', group: 'pacing', control: 'select', order: 4, showWhen: (s) => s.mode !== 'free' },
  hostRotation: { label: 'Host rotation', hint: 'Rotate moves host badge each round.', group: 'social', control: 'select', order: 0 },
  latePlayers: { label: 'Mid-set joiners', hint: 'Spectate = queue until next set.', group: 'social', control: 'select', order: 1 },
  chatEnabled: { label: 'Chat enabled', group: 'social', control: 'toggle', order: 2 },
  reactionsEnabled: { label: 'Reactions', hint: 'Floating emote glyphs during games.', group: 'social', control: 'toggle', order: 3 },
  pointMultiplier: { label: 'Point multiplier', hint: 'Affects global point contributions only.', group: 'scoring', control: 'select', order: 0 },
  lobbyPersistHours: { label: 'Lobby persists (hrs)', hint: 'Room code joinable this long after empty.', group: 'social', control: 'number', order: 4 },
  quickRejoinSec: { label: 'Quick rejoin window (s)', hint: 'Disconnected players hold their seat this long.', group: 'social', control: 'number', order: 5 },
  showTutorials: { label: 'First-time tutorials', group: 'social', control: 'toggle', order: 6 },
};

// ── Public registry shape (used by UI to introspect schema) ─────────────

export const LOBBY_SETTING_DEFS: { [K in keyof LobbySettings]: { schema: typeof LobbySettingsSchemaInternal.shape[K]; default: LobbySettings[K]; meta: FieldMeta } } = {
  mode: { schema: LobbySettingsSchemaInternal.shape.mode, default: DEFAULTS.mode, meta: FIELD_META.mode },
  eventMode: { schema: LobbySettingsSchemaInternal.shape.eventMode, default: DEFAULTS.eventMode, meta: FIELD_META.eventMode },
  roundsPerSet: { schema: LobbySettingsSchemaInternal.shape.roundsPerSet, default: DEFAULTS.roundsPerSet, meta: FIELD_META.roundsPerSet },
  winCondition: { schema: LobbySettingsSchemaInternal.shape.winCondition, default: DEFAULTS.winCondition, meta: FIELD_META.winCondition },
  targetScore: { schema: LobbySettingsSchemaInternal.shape.targetScore, default: DEFAULTS.targetScore, meta: FIELD_META.targetScore },
  allowedGames: { schema: LobbySettingsSchemaInternal.shape.allowedGames, default: DEFAULTS.allowedGames, meta: FIELD_META.allowedGames },
  voteOptions: { schema: LobbySettingsSchemaInternal.shape.voteOptions, default: DEFAULTS.voteOptions, meta: FIELD_META.voteOptions },
  voteTimerSec: { schema: LobbySettingsSchemaInternal.shape.voteTimerSec, default: DEFAULTS.voteTimerSec, meta: FIELD_META.voteTimerSec },
  roundCountdownSec: { schema: LobbySettingsSchemaInternal.shape.roundCountdownSec, default: DEFAULTS.roundCountdownSec, meta: FIELD_META.roundCountdownSec },
  pickWho: { schema: LobbySettingsSchemaInternal.shape.pickWho, default: DEFAULTS.pickWho, meta: FIELD_META.pickWho },
  hostRotation: { schema: LobbySettingsSchemaInternal.shape.hostRotation, default: DEFAULTS.hostRotation, meta: FIELD_META.hostRotation },
  latePlayers: { schema: LobbySettingsSchemaInternal.shape.latePlayers, default: DEFAULTS.latePlayers, meta: FIELD_META.latePlayers },
  chatEnabled: { schema: LobbySettingsSchemaInternal.shape.chatEnabled, default: DEFAULTS.chatEnabled, meta: FIELD_META.chatEnabled },
  reactionsEnabled: { schema: LobbySettingsSchemaInternal.shape.reactionsEnabled, default: DEFAULTS.reactionsEnabled, meta: FIELD_META.reactionsEnabled },
  pointMultiplier: { schema: LobbySettingsSchemaInternal.shape.pointMultiplier, default: DEFAULTS.pointMultiplier, meta: FIELD_META.pointMultiplier },
  lobbyPersistHours: { schema: LobbySettingsSchemaInternal.shape.lobbyPersistHours, default: DEFAULTS.lobbyPersistHours, meta: FIELD_META.lobbyPersistHours },
  quickRejoinSec: { schema: LobbySettingsSchemaInternal.shape.quickRejoinSec, default: DEFAULTS.quickRejoinSec, meta: FIELD_META.quickRejoinSec },
  showTutorials: { schema: LobbySettingsSchemaInternal.shape.showTutorials, default: DEFAULTS.showTutorials, meta: FIELD_META.showTutorials },
};

// ── Helpers ─────────────────────────────────────────────────────────────

export function defaultSettings(): LobbySettings { return { ...DEFAULTS }; }

export function applyDefaults(partial: LobbySettingsPartial | undefined): LobbySettings {
  return { ...DEFAULTS, ...(partial ?? {}) } as LobbySettings;
}

export function mergeAndValidate(existing: LobbySettingsPartial | undefined, patch: unknown): LobbySettings {
  const parsed = LobbySettingsSchema.parse(patch) as LobbySettingsPartial;
  return applyDefaults({ ...(existing ?? {}), ...parsed } as LobbySettingsPartial);
}

export function visibleFields(settings: LobbySettings): Array<{ key: keyof LobbySettings; meta: FieldMeta }> {
  return (Object.keys(FIELD_META) as Array<keyof LobbySettings>)
    .filter((k) => {
      const m = FIELD_META[k];
      return !m.showWhen || m.showWhen(settings);
    })
    .map((k) => ({ key: k, meta: FIELD_META[k] }))
    .sort((a, b) => {
      if (a.meta.group !== b.meta.group) return a.meta.group.localeCompare(b.meta.group);
      return a.meta.order - b.meta.order;
    });
}
